import React, { useRef } from "react";

import { AudioBlock } from "../AudioBlock";
import { ImageBlock } from "../ImageBlock";
import { VideoPlaybackControls } from "../VideoPlaybackControls";

import { RevisedPromptAccordion } from "./RevisedPromptAccordion";

import { CopyButton } from "@/components/ui/copy-button";
import { OutputMediaContent, OutputMediaProviderMetadata } from "@/types/prompt-blueprint";
import { cn } from "@/lib/utils";

const PARAM_KEYS = [
  "size",
  "quality",
  "background",
  "output_format",
  "aspect_ratio",
  "image_size",
] as const;

/** Format url for display: if raw base64, build data URL in frontend */
function getDisplayUrl(url: string, mimeType?: string): string {
  if (!url) {
    return "";
  }
  if (url.startsWith("data:") || url.startsWith("http")) {
    return url;
  }
  return `data:${mimeType || "image/png"};base64,${url}`;
}

function formatParams(metadata?: OutputMediaProviderMetadata | null): string {
  if (!metadata) {
    return "";
  }
  const lines: string[] = [];
  for (const key of PARAM_KEYS) {
    const value = metadata[key as keyof OutputMediaProviderMetadata];
    if (value !== null && value !== undefined && value !== "") {
      lines.push(`${key}: ${value}`);
    }
  }
  return lines.join("\n");
}

function isChatAudioSource(
  source?: OutputMediaProviderMetadata["source"] | null,
  contentId?: string | null,
): boolean {
  if (source === "chat") {
    return true;
  }
  if (source === "speech") {
    return false;
  }
  return Boolean(contentId);
}

function getHeaderLabel(
  contentId?: string | null,
  mediaType = "image",
  source?: OutputMediaProviderMetadata["source"] | null,
  mimeType?: string | null,
): string {
  if (mediaType === "video") {
    return contentId ? "video_generation" : "Video Generation";
  }
  if (mediaType === "audio") {
    if (isChatAudioSource(source, contentId)) {
      return "Audio Generated";
    }
    return contentId ? "speech_generation" : "Speech Generation";
  }
  if (mimeType === "image/svg+xml") {
    return contentId ? "image_generation" : "Vector Image";
  }
  if (contentId) {
    return "image_generation";
  }
  return "Image Generation";
}

export interface OutputMediaBlockProps {
  content: OutputMediaContent;
  /** Additional images to render in the same card (shared header/params, separate images). */
  extraImages?: OutputMediaContent[];
  readonly?: boolean;
  className?: string;
  isLoading?: boolean;
}

const OutputVideoPlayer = ({ src }: { src: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.paused) {
      void video.play();
      return;
    }
    video.pause();
  };

  return (
    <div className="flex flex-col gap-2">
      <video
        ref={videoRef}
        src={src}
        playsInline
        className="w-full cursor-pointer rounded-md"
        onClick={togglePlayback}
      />
      <VideoPlaybackControls videoRef={videoRef} />
    </div>
  );
};

/**
 * Enhanced display for image generation output (e.g. OpenAI image_generation tool).
 * Shows: header with ID, parameters block, collapsible revised prompt, and the image.
 * When `extraImages` is provided, all images share a single header and parameters block.
 */
export const OutputMediaBlock = ({
  content,
  extraImages,
  readonly = true,
  className,
  isLoading = false,
}: OutputMediaBlockProps) => {
  const { url, media_type = "image", mime_type, provider_metadata } = content;
  const displayUrl = getDisplayUrl(url ?? "", mime_type);

  if (!displayUrl || (media_type !== "image" && media_type !== "video" && media_type !== "audio")) {
    return null;
  }

  if (media_type === "audio") {
    const voice = provider_metadata?.voice ?? (content as { voice?: string }).voice;
    const audioLabel = getHeaderLabel(content.id, "audio", provider_metadata?.source);
    const displayLabel = isLoading ? "Generating Audio" : audioLabel;
    return (
      <div
        className={cn(
          "flex w-full flex-col gap-3 overflow-hidden rounded-lg border border-gray-200 bg-white",
          className,
        )}
      >
        <div className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold text-gray-900">{displayLabel}</span>
            {voice && <span className="font-mono text-xs text-gray-500">voice: {voice}</span>}
          </div>
        </div>
        <div className="px-3 pb-3">
          <AudioBlock
            media={{ type: "audio", url: displayUrl, title: displayLabel }}
            readonly
            isLoading={isLoading}
          />
        </div>
      </div>
    );
  }

  if (media_type === "video") {
    return (
      <div
        className={cn(
          "flex w-full flex-col gap-3 overflow-hidden rounded-lg border border-gray-200 bg-white",
          className,
        )}
      >
        <div className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold text-gray-900">
              {getHeaderLabel(content.id, "video")}
            </span>
            {content.id && (
              <span className="truncate font-mono text-xs text-gray-500" title={content.id}>
                {content.id}
              </span>
            )}
          </div>
        </div>
        <div className="px-3 pb-3">
          <OutputVideoPlayer src={displayUrl} />
        </div>
      </div>
    );
  }

  const paramsText = formatParams(provider_metadata);
  const hasParams = paramsText.length > 0;

  const allImages: {
    displayUrl: string;
    revisedPrompt?: string;
    id?: string;
  }[] = [
    {
      displayUrl,
      revisedPrompt:
        provider_metadata?.revised_prompt ??
        (content as { revised_prompt?: string }).revised_prompt ??
        undefined,
      id: content.id,
    },
    ...(extraImages ?? [])
      .map(extra => ({
        displayUrl: getDisplayUrl(extra.url ?? "", extra.mime_type),
        revisedPrompt:
          extra.provider_metadata?.revised_prompt ??
          (extra as { revised_prompt?: string }).revised_prompt ??
          undefined,
        id: extra.id,
      }))
      .filter(img => img.displayUrl),
  ];

  const isGrouped = allImages.length > 1;

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-3 overflow-hidden rounded-lg border border-gray-200 bg-white",
        className,
      )}
    >
      {/* Header */}
      <div className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-gray-900">
            {getHeaderLabel(content.id, media_type, provider_metadata?.source, mime_type)}
          </span>
          {content.id && !isGrouped && (
            <span className="truncate font-mono text-xs text-gray-500" title={content.id}>
              {content.id}
            </span>
          )}
        </div>
      </div>

      {/* Parameters block – shown once for all images */}
      {hasParams && (
        <div className="group/params px-3 pb-1">
          <div className="relative rounded-md border border-gray-200 bg-gray-50/50 px-3 py-2 font-mono text-sm">
            <pre className="whitespace-pre-wrap text-gray-700">{paramsText}</pre>
            <div className="absolute right-2 top-2 z-10">
              <CopyButton
                text={paramsText}
                className="bg-white/70 opacity-0 transition-opacity duration-200 group-hover/params:opacity-100"
                title="Copy parameters"
              />
            </div>
          </div>
        </div>
      )}

      {/* Generated image(s) */}
      {allImages.map((img, idx) => (
        <React.Fragment key={img.id ?? img.displayUrl ?? idx}>
          {img.revisedPrompt?.trim() && (
            <RevisedPromptAccordion revisedPrompt={img.revisedPrompt} label="Revised prompt" />
          )}
          <div className="px-3 pb-3">
            <ImageBlock
              media={{
                type: "image",
                url: img.displayUrl,
                title: "Generated image",
              }}
              readonly={readonly}
              size="md"
            />
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};
